// Port of Weather/DemoWeatherProvider.swift: deterministic synthetic forecast for demo mode, tests and offline
// development.
//
// Output is a pure function of (location, elevation, UTC day of `now`, scenario): 72 hourly values from 00:00 UTC of
// the current day plus 3 daily summaries. Temperatures follow a seasonal mean at a 1500 m reference, a diurnal curve
// peaking ~2.5 h after local solar noon, and are moved to the requested elevation with the standard lapse rate.
// Sunrise/sunset come from the solar calculator (rounded to the minute). Every forecast is flagged synthetic with
// synthetic-demo provenance — it must never be presented as a real forecast.

import type { DailyWeather, EpochMs, GeoCoordinate, WeatherForecast, WeatherSnapshot } from '../core/models';
import { syntheticDemoProvenance } from '../core/domain';
import { isValidCoordinate } from '../core/geo';
import { daylight } from '../core/solar';
import { LapseRate, roundHalfAwayFromZero } from './lapseRate';
import { ThunderstormRiskModel, WMOWeatherCode } from './weatherCodes';
import { WeatherFailure, type WeatherProvider } from './weatherProvider';

/** Weather situation to simulate. */
export type DemoWeatherScenario =
  /** High pressure: dry, light winds, warming trend. No thunderstorm risk. */
  | 'settled'
  /** A cold front crosses on day 2 (rain, snow high up, strong wind), post-frontal showers on day 3. */
  | 'changeable'
  /** Warm, unstable air: clear mornings, afternoon convection with thunderstorms (≈ 14–17 UTC) every day. */
  | 'thunderstorms';

export const allDemoWeatherScenarios: readonly DemoWeatherScenario[] = ['settled', 'changeable', 'thunderstorms'];

const HOUR = 3_600_000;
const DAY = 86_400_000;

/** Sky/precipitation state for one hour, before temperature-dependent precipitation typing. */
interface Sky {
  cloudCover: number;
  precipitationProbability: number;
  precipitation: number;
  cape: number;
  /** WMO code; precipitation codes are re-typed to snow when cold. */
  code: number;
  extraGust: number;
}

const sky = (
  cloudCover: number,
  precipitationProbability: number,
  precipitation: number,
  cape: number,
  code: number,
  extraGust = 0,
): Sky => ({ cloudCover, precipitationProbability, precipitation, cape, code, extraGust });

const U64 = (1n << 64n) - 1n;

function splitMix(input: bigint): bigint {
  let z = (input + 0x9e3779b97f4a7c15n) & U64;
  z = ((z ^ (z >> 30n)) * 0xbf58476d1ce4e5b9n) & U64;
  z = ((z ^ (z >> 27n)) * 0x94d049bb133111ebn) & U64;
  return z ^ (z >> 31n);
}

export class DemoWeatherProvider implements WeatherProvider {
  /** Altitude (m) the synthetic climatology is defined at. */
  static readonly referenceElevation = 1500;
  /** Number of forecast days produced. */
  static readonly forecastDays = 3;

  readonly providerID = 'demo-synthetic';
  readonly scenario: DemoWeatherScenario;
  private readonly now: () => EpochMs;

  constructor(options: { now?: () => EpochMs; scenario?: DemoWeatherScenario } = {}) {
    this.now = options.now ?? Date.now;
    this.scenario = options.scenario ?? 'changeable';
  }

  async forecast(location: GeoCoordinate, elevation?: number): Promise<WeatherForecast> {
    if (!isValidCoordinate(location)) throw new WeatherFailure({ kind: 'noData' });
    return this.makeForecast(location, elevation, this.now());
  }

  /** Synchronous generation (previews, tests). `now` stamps `fetchedAt` and selects the UTC day. */
  makeForecast(location: GeoCoordinate, elevation: number | undefined, now: EpochMs): WeatherForecast {
    const targetElevation =
      elevation !== undefined && Number.isFinite(elevation) ? elevation : DemoWeatherProvider.referenceElevation;
    const firstDay = Math.floor(now / DAY);
    const dayStart = firstDay * DAY;
    const latKey = roundHalfAwayFromZero(location.latitude * 100);
    const lonKey = roundHalfAwayFromZero(location.longitude * 100);

    const hourly: WeatherSnapshot[] = [];
    for (let h = 0; h < DemoWeatherProvider.forecastDays * 24; h++) {
      const dayIndex = Math.floor(h / 24);
      const hour = h % 24;
      const time = dayStart + h * HOUR;
      const seed = [latKey, lonKey, firstDay + dayIndex, hour, this.scenarioSeed()];
      hourly.push(this.snapshot(time, dayIndex, hour, location, targetElevation, DemoWeatherProvider.noise(seed)));
    }

    const daily: DailyWeather[] = [];
    for (let dayIndex = 0; dayIndex < DemoWeatherProvider.forecastDays; dayIndex++) {
      const date = dayStart + dayIndex * DAY;
      const hours = hourly.slice(dayIndex * 24, (dayIndex + 1) * 24);
      const light = daylight(date + 12 * HOUR, location);
      const day: DailyWeather = {
        date,
        temperatureMin: Math.min(...hours.map((s) => s.temperature)),
        temperatureMax: Math.max(...hours.map((s) => s.temperature)),
        precipitationSum: hours.reduce((sum, s) => sum + (s.precipitationAmount ?? 0), 0),
        maxWindGust: Math.max(...hours.flatMap((s) => (s.windGust !== undefined ? [s.windGust] : []))),
        thunderstormRisk: ThunderstormRiskModel.dailyRisk(hours.map((s) => s.thunderstormRisk)),
      };
      if (light.sunrise !== undefined) day.sunrise = DemoWeatherProvider.roundedToMinute(light.sunrise);
      if (light.sunset !== undefined) day.sunset = DemoWeatherProvider.roundedToMinute(light.sunset);
      daily.push(day);
    }

    return {
      location: { latitude: location.latitude, longitude: location.longitude },
      elevation: targetElevation,
      modelElevation: DemoWeatherProvider.referenceElevation,
      hourly,
      daily,
      fetchedAt: now,
      isSynthetic: true,
      provenance: syntheticDemoProvenance(`Demo weather (${this.scenario})`, now),
    };
  }

  // MARK: Model

  private snapshot(
    time: EpochMs,
    dayIndex: number,
    hour: number,
    location: GeoCoordinate,
    targetElevation: number,
    noise: number,
  ): WeatherSnapshot {
    // Temperature at the 1500 m reference.
    const dayOfYear = DemoWeatherProvider.dayOfYear(time);
    const seasonalMean = 5.5 + 8.5 * Math.sin((2 * Math.PI * (dayOfYear - 110)) / 365);
    const latitudeEffect = -0.6 * (location.latitude - 46.5);
    const solarNoonUTC = 12 - location.longitude / 15;
    const diurnal = this.diurnalAmplitude(dayIndex) * Math.cos((2 * Math.PI * (hour - (solarNoonUTC + 2.5))) / 24);
    const referenceTemperature = seasonalMean + latitudeEffect + this.dayOffset(dayIndex) + diurnal + 0.3 * noise;
    const temperature = LapseRate.adjust(referenceTemperature, DemoWeatherProvider.referenceElevation, targetElevation);

    const s = this.sky(dayIndex, hour, noise);
    if (s.precipitation > 0 && temperature <= 1) {
      switch (s.code) {
        case 80:
        case 81:
        case 82:
          s.code = 85; // snow showers
          break;
        case 61:
        case 51:
          s.code = 71;
          break;
        case 63:
        case 53:
          s.code = 73;
          break;
        case 65:
        case 55:
          s.code = 75;
          break;
      }
    }

    // Wind: stronger higher up and in the afternoon.
    const heightFactor = 1 + (Math.max(0, targetElevation - DemoWeatherProvider.referenceElevation) / 1000) * 0.35;
    const afternoon = 1 + 0.25 * Math.sin((2 * Math.PI * (hour - 8)) / 24);
    const windSpeed = Math.max(0, this.baseWind(dayIndex) * heightFactor * afternoon * (1 + 0.1 * noise));
    const windGust = Math.max(windSpeed * 1.6, s.extraGust > 0 ? s.extraGust + 5 * noise : 0);
    const windDirection = (this.prevailingDirection() + 15 * noise + 360) % 360;

    let feelsLike: number;
    if (temperature <= 10 && windSpeed >= 4.8) {
      const v = windSpeed ** 0.16;
      feelsLike = 13.12 + 0.6215 * temperature - 11.37 * v + 0.3965 * temperature * v; // wind chill (km/h)
    } else {
      feelsLike = temperature;
    }

    return {
      time,
      temperature,
      feelsLike,
      precipitationProbability: s.precipitationProbability,
      precipitationAmount: s.precipitation,
      windSpeed,
      windGust,
      windDirection,
      thunderstormRisk: ThunderstormRiskModel.risk(s.code, s.cape, s.precipitationProbability, s.precipitation),
      // Consistent with the lapse rate: altitude where the reference temperature reaches 0 °C.
      freezingLevel: Math.max(0, DemoWeatherProvider.referenceElevation + referenceTemperature / LapseRate.standard),
      cloudCover: s.cloudCover,
      condition: WMOWeatherCode.condition(s.code),
    };
  }

  private sky(dayIndex: number, hour: number, noise: number): Sky {
    switch (this.scenario) {
      case 'settled': {
        const cumulus = hour >= 12 && hour <= 16;
        const cover = (cumulus ? 0.25 : 0.05) + 0.05 * noise;
        return sky(Math.max(0, cover), 0.05, 0, cumulus ? 300 : 120, cover < 0.2 ? 0 : 2);
      }
      case 'changeable': {
        if (dayIndex === 0) {
          const cover = Math.min(1, 0.3 + (0.6 * hour) / 23);
          return sky(cover, 0.1 + (0.3 * hour) / 23, 0, 200, cover < 0.7 ? 2 : 3);
        }
        if (dayIndex === 1) {
          if (hour >= 4 && hour <= 16) {
            // frontal rain band
            const heavy = hour >= 8 && hour <= 11;
            const amount = heavy ? 2.0 + 0.3 * noise : 0.8 + 0.2 * noise;
            return sky(1, heavy ? 0.9 : 0.8, amount, 100, heavy ? 63 : 61, heavy ? 60 : 45);
          }
          return sky(0.9, 0.45, 0.1, 80, 51);
        }
        if (hour >= 11 && hour <= 16) {
          // post-frontal showers
          return sky(0.6, 0.4, 0.4 + 0.1 * noise, 500, 80);
        }
        return sky(0.35, 0.15, 0, 150, 2);
      }
      case 'thunderstorms': {
        const peakCAPE = [1800, 2600, 2200][Math.min(dayIndex, 2)];
        const cape = hour >= 8 && hour <= 20 ? Math.max(50, peakCAPE * Math.sin((Math.PI * (hour - 8)) / 12)) : 50;
        if (hour === 12) return sky(0.5, 0.3, 0, cape, 2);
        if (hour === 13 || hour === 18) return sky(0.75, 0.5, 0.6 + 0.2 * noise, cape, 80, 45);
        if (hour >= 14 && hour <= 17) return sky(0.9, 0.75, 2.5 + 1.0 * noise, cape, 95, 70);
        if (hour === 19) return sky(0.6, 0.3, 0.1, cape, 3);
        if (hour >= 20 && hour <= 23) return sky(0.35, 0.1, 0, cape, 2);
        const cover = Math.max(0, 0.1 + 0.05 * noise);
        return sky(cover, 0.05, 0, cape, cover < 0.2 ? 0 : 1);
      }
    }
  }

  /** Day-to-day temperature departure from the seasonal mean (°C). */
  private dayOffset(dayIndex: number): number {
    const offsets = {
      settled: [1.5, 2.0, 2.5],
      changeable: [0.5, -3.0, -1.5],
      thunderstorms: [3.0, 3.5, 3.0],
    }[this.scenario];
    return offsets[Math.min(dayIndex, offsets.length - 1)];
  }

  /** Half of the daily temperature range (°C). */
  private diurnalAmplitude(dayIndex: number): number {
    switch (this.scenario) {
      case 'settled':
        return 5.0;
      case 'changeable':
        return [3.5, 1.5, 3.0][Math.min(dayIndex, 2)];
      case 'thunderstorms':
        return 4.5;
    }
  }

  /** Mean 10 m wind at the reference altitude (km/h). */
  private baseWind(dayIndex: number): number {
    const winds = { settled: [8, 7, 9], changeable: [14, 26, 18], thunderstorms: [9, 10, 12] }[this.scenario];
    return winds[Math.min(dayIndex, winds.length - 1)];
  }

  private prevailingDirection(): number {
    switch (this.scenario) {
      case 'settled':
        return 60; // light bise
      case 'changeable':
        return 245; // westerly flow
      case 'thunderstorms':
        return 210;
    }
  }

  // MARK: Deterministic helpers

  private scenarioSeed(): number {
    return { settled: 1, changeable: 2, thunderstorms: 3 }[this.scenario];
  }

  /** Whole minutes, like the sun times of real providers. */
  private static roundedToMinute(date: EpochMs): EpochMs {
    return Math.round(date / 60_000) * 60_000;
  }

  private static dayOfYear(date: EpochMs): number {
    const d = new Date(date);
    const dayStart = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
    return Math.floor((dayStart - Date.UTC(d.getUTCFullYear(), 0, 1)) / DAY) + 1;
  }

  /** Stable pseudo-random value in -1…1 (SplitMix64; identical to the Swift sequence). */
  static noise(values: readonly number[]): number {
    let state = 0x243f6a8885a308d3n;
    for (const value of values) state = splitMix(state ^ BigInt.asUintN(64, BigInt(Math.trunc(value))));
    return (Number(state >> 11n) / 2 ** 53) * 2 - 1;
  }
}
